import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import find from 'lodash/find';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Button, makeStyles } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import {
  FILTER_ENTITY_TYPE_API,
  FILTER_ENTITY_TYPE_API_PER_ORG,
  FILTER_ENTITY_TYPE_APP,
  FILTER_ENTITY_TYPE_ORG,
  FILTER_TYPE_RANK,
  FILTER_TYPE_RANK_API_PER_ORG,
  FILTER_TYPE_NAME,
  API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
  FILTER_TYPE_NAME_API_PER_ORG,
  API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API,
  API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG,
} from '../../../constants/filter';
import { getFilterTitle, encodeMetricsFilter, getSelectedFilters } from '../utils/filter';
import UsageFilter from '../UsageFilter';
import styles from './styles';
import { getI18n } from "../../../utils/intl";

const getDefaultFilterData = (entityType) => {
  if (entityType === FILTER_ENTITY_TYPE_API_PER_ORG) {
    return ({
      filterType: FILTER_TYPE_RANK_API_PER_ORG,
      rank: 3,
      rankType: API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
    });
  }
  return ({
    filterType: FILTER_TYPE_RANK,
    rank: 3,
  });
};

const getFilterName = (key) => {
  let name = '';
  switch (key) {
    case FILTER_ENTITY_TYPE_API:
      name = 'APIs';
      break;
    case FILTER_ENTITY_TYPE_APP:
      name = 'Apps';
      break;
    case FILTER_ENTITY_TYPE_ORG:
      name = 'Orgs';
      break;
    case FILTER_ENTITY_TYPE_API_PER_ORG:
      name = 'Apis per Orgs';
      break;
    default: return '';
  }
  return name;
}

const MAX_ALLOWED_SLOTS = 10;

const UsageFilterWrapper = (props) => {
  const {
    selectedMetrics, userOrgUuid, isDeveloper, isOrgAdmin, isOrgPublisher, apiPerOrgList,
    handleFiltersChange: handleFiltersChangeProp, updateMetricFilters, orgMap, apiMap,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const getInitialSelectedFilterState = () => {
    if (!isUndefined(selectedMetrics) && !isEmpty(selectedMetrics)) {
      return [...selectedMetrics];
    } else {
      return getDefaultSelectedFilterState();
    }
  }
  
  const getDefaultOrgUserFilterState = () => {
    return {
      entityType: FILTER_ENTITY_TYPE_ORG,
      filterType: FILTER_TYPE_NAME,
      selectedItems: {
        [userOrgUuid]: 1,
      },
      selectedItemsCount: 1,
    };
  };

  const getDefaultSelectedFilterState = () => {
    if (isDeveloper || isOrgAdmin || isOrgPublisher) {
      return [getDefaultOrgUserFilterState()];
    }
    const defaultInternalUserState = {
      filterType: FILTER_TYPE_RANK,
      rank: 3,
      entityType: FILTER_ENTITY_TYPE_API,
    };
    return [defaultInternalUserState];
  };

  const [state, updateState] = useState({
    isDropdownOpen: false,
    popUpFilterEntityType: '',
    inFilterEditMode: false,
    selectedFiltersState: getInitialSelectedFilterState(),
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClick, false);
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('mousedown', handleClick, false);
      document.removeEventListener('keydown', handleKeyDown, false);
    }
  }, []);

  const getCollapsedFilterLabel = selectedFilterState => {
    const name = getFilterName(selectedFilterState.entityType);
    const { filterType, rank, selectedItems, rankType } = selectedFilterState;
    const itemCount = size(selectedItems);
    if ((filterType === FILTER_TYPE_NAME) ||
    (filterType === FILTER_TYPE_NAME_API_PER_ORG)) {
      return intl.getI18nMessage(
        'label.analytics.filter.filterByName.collapsedFilterLabel.text',
        { itemCount, name },
      );
    } else if (filterType === FILTER_TYPE_RANK) {
      return intl.getI18nMessage(
        'label.analytics.filter.filterByRank.collapsedFilterLabel.text',
        { rank, name },
      );
    } else if (filterType === FILTER_TYPE_RANK_API_PER_ORG) {
      let message;
      if (rankType === API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK) {
        message = intl.getI18nMessage(
          'label.analytics.filter.filterByRank.collapsedFilterLabel.text',
          { rank, name },
        );
      } else if (rankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API) {
        message = intl.getI18nMessage(
          'label.analytics.filter.filterByRank.collapsedFilterLabel.apiperorg.rankfororg.text',
          { rank, itemCount },
        );
      } else if (rankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG) {
        message = intl.getI18nMessage(
          'label.analytics.filter.filterByRank.collapsedFilterLabel.apiperorg.rankforapi.text',
          { rank, itemCount },
        );
      }
      return message;
    }
    return '';
  };

  const isFilterAvailable = (entityType) => {
    const isItIncluded = state.selectedFiltersState.filter(
      filterSelected => filterSelected.entityType === entityType,
    ).length;
    return !isItIncluded;
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 27) {
      setState({
        isDropdownOpen: false,
      });
    }
  };

  const handleClick = (event) => {
    if (event.which === 1) {
      const targetElement = event.target;
      const filterContainer = targetElement.closest('#filter-container');
      const filterBtn = targetElement.closest('#filter-btn');
      if (isNull(filterContainer) && isNull(filterBtn)) {
        setState({ isDropdownOpen: false });
      }
    }
  };

  const showFilterPopUp = (type) => {
    setState(prevState => ({
      popUpFilterEntityType: type,
      showFilterPopUp: !prevState.showFilterPopUp,
      isDropdownOpen: false,
    }));
  };

  const removeFilterPopUp = () => {
    setState({
      inFilterEditMode: false,
      showFilterPopUp: false,
    });
  };

  const toggleAddFilterDropdown = () => {
    setState(prevState => ({
      isDropdownOpen: !prevState.isDropdownOpen,
      showFilterPopUp: false,
      inFilterEditMode: false,
    }));
  };

  const editFilter = (key) => {
    setState(prevState => ({
      inFilterEditMode: !prevState.inFilterEditMode,
    }));
    showFilterPopUp(key);
  }

  const handleFiltersChange = () => {
    const { selectedFiltersState } = state;
    const selectedFilters = getSelectedFilters(selectedFiltersState) || {};
    selectedFilters.entityType = selectedFiltersState[0].entityType;
    handleFiltersChangeProp(selectedFilters);
    updateMetricFilters(encodeMetricsFilter(selectedFiltersState));
  }

  useEffect(() => {
    removeFilterPopUp();
    handleFiltersChange();
  }, [state.selectedFiltersState]);

  const handleFilterChange = (filterState) => {
    setState({
      selectedFiltersState: [filterState],
    });
  };

  const handleFilterSelection = (type) => {
    // update the filter to pull data for userOrg,
    // as orgUsers are not allowed to view other orgs' data
    if ((type === FILTER_ENTITY_TYPE_ORG)
      && (isDeveloper || isOrgAdmin || isOrgPublisher)
    ) {
      const orgFilterState = getDefaultOrgUserFilterState();
      setState({
        selectedFiltersState: [orgFilterState],
        isDropdownOpen: false,
      });
      return;
    }

    showFilterPopUp(type);
  };

  const renderNewFilterDropdown = (classes) => {
    const labelsMap = {
      [FILTER_ENTITY_TYPE_API]: 'label.analyticsDashboard.filter.dropdown.add.api',
      [FILTER_ENTITY_TYPE_API_PER_ORG]: 'label.analyticsDashboard.filter.dropdown.add.apiperorg',
      [FILTER_ENTITY_TYPE_ORG]: 'label.analyticsDashboard.filter.dropdown.add.organization',
    };
    const entites = [];
    if (!isOrgAdmin && !isDeveloper) {
      entites.push(FILTER_ENTITY_TYPE_API);
    }
    entites.push(FILTER_ENTITY_TYPE_ORG);
    entites.push(FILTER_ENTITY_TYPE_API_PER_ORG);

    const renderBtn = (item) => (
      <Button
        className={clsx(
          classes.filterTypeBtn,
          isFilterAvailable(item) ? null : classes.filterTypeBtn__disabled,
        )}
        key={item}
        onClick={() => handleFilterSelection(item)}
      >
        {intl.getI18nMessage(labelsMap[item])}
      </Button>
    );

    return (
      <div
        className={classes.addFilterDropdown}
        data-apim-test={'add-filter-dropdown'}
      >
        {entites.map(item => renderBtn(item))}
      </div>
    );
  }

  const renderFilterPopup = () => {
    if (!state.showFilterPopUp) {
      return (<div />);
    }
    const popUpFilterEntityType = state.popUpFilterEntityType;
    const title = getFilterTitle(popUpFilterEntityType, intl);
    let nameMap = {};
    if (popUpFilterEntityType === FILTER_ENTITY_TYPE_ORG) {
      nameMap = orgMap;
    } else if (popUpFilterEntityType === FILTER_ENTITY_TYPE_API) {
      nameMap = apiMap;
    } else if (popUpFilterEntityType === FILTER_ENTITY_TYPE_API_PER_ORG) {
      const apiPerOrgArr = apiPerOrgList || [];
      apiPerOrgArr.forEach(item => {
        const id = `${item.apiUuid}##${item.organizationUuid}`;
        const itemData = {};
        itemData.primaryId = item.apiUuid;
        itemData.secondaryId = item.organizationUuid;
        itemData.name = item.apiName;
        itemData.secondaryName = item.organizationName;
        nameMap[id] = itemData;
      });
    }
    const selectedFilterState = find(state.selectedFiltersState,
      filterState => filterState.entityType === popUpFilterEntityType) ||
      getDefaultFilterData(popUpFilterEntityType);
    return (
      <UsageFilter
        closeFilter={removeFilterPopUp}
        entityNameMap={nameMap}
        entityType={popUpFilterEntityType}
        title={title}
        filterInitState={{ ...selectedFilterState }}
        showFilter={state.showFilterPopUp}
        handleFilterChange={handleFilterChange}
        maxAllowedEntities={MAX_ALLOWED_SLOTS}
        isDeveloper={isDeveloper}
        isOrgAdmin={isOrgAdmin}
        isOrgPublisher={isOrgPublisher}
        userOrgUuid={userOrgUuid}
      />
    );
  };

  const { selectedFiltersState } = state;
  let isFilterEditable = true;
  const isOrgFilterSelected = find(selectedFiltersState,
    filter => filter.entityType === FILTER_ENTITY_TYPE_ORG);
  if (isOrgFilterSelected && (isDeveloper || isOrgAdmin || isOrgPublisher)) {
    isFilterEditable = false;
  }
  return (
    <div className={classes.filterWrapper} id={'filter-btn'}>
      <div className={classes.filterTitle}>
        {intl.getI18nMessage('label.analytics.usage.filter.dropdown.title')}
      </div>
      <Button
        className={classes.addFilterBtn}
        data-apim-test={'add-filter-button'}
        onClick={toggleAddFilterDropdown}
        endIcon={
          <ArrowDropDownIcon
            fontSize='small'
            className={clsx(classes.icon, { [classes.dropdownOpen]: state.isDropdownOpen })}
          />
        }
      >
        {intl.getI18nMessage('label.analyticsDashboard.filter.dropdown.label.select')}
      </Button>
      {selectedFiltersState &&
        selectedFiltersState.map(value => (
          <div className={classes.editFilterContainer} id={'filter-container'} key={value.entityType}>
            <Button
              className={classes.popupBtn}
              onClick={() => editFilter(value.entityType)}
              disabled={!isFilterEditable ? 'true' : ''}
            >
              {getCollapsedFilterLabel(value)}
            </Button>
            {state.inFilterEditMode &&
              state.popUpFilterEntityType === value.entityType &&
              renderFilterPopup()}
          </div>
        ))}
      {state.isDropdownOpen && renderNewFilterDropdown(classes)}
      {!state.inFilterEditMode && renderFilterPopup()}
    </div>
  );
}

UsageFilterWrapper.propTypes = {
  apiMap: PropTypes.object,
  apiPerOrgList: PropTypes.arrayOf(PropTypes.object),
  orgMap: PropTypes.object,
  handleFiltersChange: PropTypes.func,
  updateMetricFilters: PropTypes.func,
  selectedMetrics: PropTypes.arrayOf(PropTypes.object),
  isDeveloper: PropTypes.bool,
  isOrgAdmin: PropTypes.bool,
  isOrgPublisher: PropTypes.bool,
  userOrgUuid: PropTypes.string,
};

export default UsageFilterWrapper;
